import { MapPin } from "lucide-react";

type Props = {
  cityName: string;
  cityState: string;
  neighborhoods: string[];
  zips?: string[];
  nearbyCities?: string[];
};

function ChipGroup({ label, items }: { label: string; items: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
        {label}
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {items.map((it) => (
          <li
            key={it}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground/85 shadow-card"
          >
            <MapPin className="h-3 w-3 text-brand-gold-deep" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CityCoverageBlock({
  cityName,
  cityState,
  neighborhoods,
  zips = [],
  nearbyCities = [],
}: Props) {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    `${cityName}, ${cityState}`,
  )}&z=11&output=embed`;

  return (
    <section className="border-y border-border bg-section-alt py-14 lg:py-20">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
            Service area
          </p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Where we serve in {cityName}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Neighborhoods, ZIP codes, and nearby towns we actively rank and
            convert for in the {cityName} market.
          </p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:gap-10">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <iframe
              title={`Map of ${cityName}, ${cityState} — Colorado Web Impressions coverage`}
              src={mapSrc}
              width="100%"
              height="420"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full border-0"
            />
          </div>

          <div className="space-y-6">
            <ChipGroup label="Neighborhoods" items={neighborhoods} />
            <ChipGroup label="ZIP codes" items={zips} />
            <ChipGroup label="Nearby cities" items={nearbyCities} />
          </div>
        </div>
      </div>
    </section>
  );
}
